import { combineReducers, configureStore, ThunkAction } from "@reduxjs/toolkit";
import { createWrapper } from "next-redux-wrapper";
import { Action } from "redux";

import { adapterSlice } from "../features/adapter/slice";
import { anonymousUserSlice } from "../features/anonymous-user/slice";
import { projectFinderSlice } from "../features/project-finder/slice";
import { projectsSlice } from "../features/projects/slice";
import { quickPollSlice } from "../features/quick-poll/slice";
import { userSlice } from "../features/user/slice";
import { siteSlice } from "../features/site/slice";
import { surveySlice } from "../features/survey/slice";
import { siteFeaturesSlice } from "../features/site-features/slice";
import { homePageRevisionSlice } from "../features/home-page-revision/slice";
import { navigationLinksSlice } from "../features/navigation-links/slice";
import { staticPageSlice } from "../features/static-page/slice";
import { sitemapSlice } from "../features/sitemap/slice";
import { themeSlice } from "../features/theme/slice";
import { messageTextSlice } from "../features/message-text/slice";
import { settingsSlice } from "../features/settings/slice";
import { signupFormQuestionsSlice } from "../features/signup-form-questions/slice";
import { registrationsSlice } from "../features/registrations/slice";
import { questionsSlice } from "../features/questions/slice";
import { visitsLoggingSlice } from "../features/visits-logging/slice";
import { recaptchaSlice } from "../features/recaptcha/slice";
import { userConfirmationSlice } from "../features/user-confirmation/slice";
import { featuresSlice } from "../features/features/slice";
import { usersSlice } from "../features/users/slice";
import { participationConfigurationsSlice } from "../features/participation-configurations/slice";
import { activitiesSlice } from "../features/activities/slice";
import { recommendationsSlice } from "../features/recommendations/slice";
import { userSubscriptionsSlice } from "../features/user-subscriptions/slice";
import { liquidTemplatesSlice } from "../features/liquid-templates/slice";
import { signupFormResponsesSlice } from "../features/signup-form-responses/slice";
import { projectSlice } from "../features/project/slice";
import { brainstormersSlice } from "../features/brainstormers/slice";
import { placeSlice } from "../features/place/slice";
import { placeLayersSlice } from "../features/place-layers/slice";
import { markerCategoriesSlice } from "../features/place-marker-category/slice";
import { placesMarkerSlice } from "../features/place-marker/slice";
import { placesSurveyQuestionsSlice } from "../features/place-form/slice";
import { placesRightPanelView } from "../features/place-right-panel/slice";
import { placeMarkersSlice } from "../features/place-markers/slice";
import { toolCustomizationsSlice } from "../features/tool-customizations/slice";
import { googleTranslateIntegrationSlice } from "../features/google-translate/slice";
import { unverifiedModalSlice } from "../features/unverified-modal/slice";
import { cookieConsentSlice } from "../features/cookie-consent/slice";
import { authGateSlice } from "../features/profile-gate/slice";
import { widgetsSlice } from "../features/widgets/slice";
import { layoutSlice } from "../features/layout/slice";

const rootReducer = combineReducers({
  [adapterSlice.name]: adapterSlice.reducer,
  [anonymousUserSlice.name]: anonymousUserSlice.reducer,
  [quickPollSlice.name]: quickPollSlice.reducer,
  [surveySlice.name]: surveySlice.reducer,
  [projectsSlice.name]: projectsSlice.reducer,
  [siteSlice.name]: siteSlice.reducer,
  [projectFinderSlice.name]: projectFinderSlice.reducer,
  [siteFeaturesSlice.name]: siteFeaturesSlice.reducer,
  [homePageRevisionSlice.name]: homePageRevisionSlice.reducer,
  [navigationLinksSlice.name]: navigationLinksSlice.reducer,
  [staticPageSlice.name]: staticPageSlice.reducer,
  [sitemapSlice.name]: sitemapSlice.reducer,
  [themeSlice.name]: themeSlice.reducer,
  [messageTextSlice.name]: messageTextSlice.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [signupFormQuestionsSlice.name]: signupFormQuestionsSlice.reducer,
  [registrationsSlice.name]: registrationsSlice.reducer,
  [questionsSlice.name]: questionsSlice.reducer,
  [visitsLoggingSlice.name]: visitsLoggingSlice.reducer,
  [recaptchaSlice.name]: recaptchaSlice.reducer,
  [userConfirmationSlice.name]: userConfirmationSlice.reducer,
  [featuresSlice.name]: featuresSlice.reducer,
  [usersSlice.name]: usersSlice.reducer,
  [userSlice.name]: userSlice.reducer,
  [participationConfigurationsSlice.name]:
    participationConfigurationsSlice.reducer,
  [activitiesSlice.name]: activitiesSlice.reducer,
  [recommendationsSlice.name]: recommendationsSlice.reducer,
  [userSubscriptionsSlice.name]: userSubscriptionsSlice.reducer,
  [liquidTemplatesSlice.name]: liquidTemplatesSlice.reducer,
  [signupFormResponsesSlice.name]: signupFormResponsesSlice.reducer,
  [projectSlice.name]: projectSlice.reducer,
  [brainstormersSlice.name]: brainstormersSlice.reducer,
  [placeSlice.name]: placeSlice.reducer,
  [placeLayersSlice.name]: placeLayersSlice.reducer,
  [markerCategoriesSlice.name]: markerCategoriesSlice.reducer,
  [placesRightPanelView.name]: placesRightPanelView.reducer,
  [placesMarkerSlice.name]: placesMarkerSlice.reducer,
  [placesSurveyQuestionsSlice.name]: placesSurveyQuestionsSlice.reducer,
  [placeMarkersSlice.name]: placeMarkersSlice.reducer,
  [toolCustomizationsSlice.name]: toolCustomizationsSlice.reducer,
  [googleTranslateIntegrationSlice.name]:
    googleTranslateIntegrationSlice.reducer,
  [unverifiedModalSlice.name]: unverifiedModalSlice.reducer,
  [cookieConsentSlice.name]: cookieConsentSlice.reducer,
  [authGateSlice.name]: authGateSlice.reducer,
  [widgetsSlice.name]: widgetsSlice.reducer,
  [layoutSlice.name]: layoutSlice.reducer
});

export type AppState = ReturnType<typeof rootReducer>;

export const createStore = (preloadedState?: AppState) =>
  configureStore({
    reducer: rootReducer,
    preloadedState
  });

const store = createStore();

export type AppStore = ReturnType<typeof createStore>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  AppState,
  unknown,
  Action
>;

export type AppDispatch = typeof store.dispatch;
export const wrapper = createWrapper<AppStore>(() => createStore());
